import { useTranslation } from 'react-i18next';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Label, cn, Button } from '@c2fo/liquidity';
import { XmarkIcon } from '@c2fo/liquidity/icons';
import { useReporting } from '@/reporting';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { useOfferActivityFilters } from './OfferActivityFilterContext';
import { PARTICIPATION_FILTER } from './useOfferActivityFilterLogic';

const OfferActivityFilters = () => {
  const { t } = useTranslation();
  const { asNumber } = useLocaleFormat();
  const { trackEnterpriseEvent } = useReporting();
  const {
    clearFilters,
    filters,
    availableCustomers,
    totalCustomerCount,
    totalDivisionCount,
    filteredCustomerCount,
    filteredDivisionCount,
    setMakerOrganization,
    setParticipation,
  } = useOfferActivityFilters();

  const handleCustomerChange = (value: string) => {
    const previousValue = filters.customer || 'all';

    if (value === 'all') {
      setMakerOrganization('');
    } else {
      setMakerOrganization(value);
    }

    trackEnterpriseEvent('offer-activity::filter::changed', {
      filterType: 'customer',
      previousValue,
      newValue: value,
    });
  };

  const handleParticipationChange = (value: string) => {
    const previousValue = filters.participation || 'all';

    if (value === 'all') {
      setParticipation('');
    } else {
      setParticipation(value, 'filter');
    }

    trackEnterpriseEvent('offer-activity::filter::changed', {
      filterType: 'participation',
      previousValue,
      newValue: value,
    });
  };

  const handleClearFilters = () => {
    clearFilters();

    trackEnterpriseEvent('offer-activity::filter::cleared');
  };

  // only show the filter value in dropdown if it originated from dropdown
  const participationDropdownValue = filters.participationSource === 'filter' ? filters.participation || 'all' : 'all';
  // check if any filter is applied from a card
  const hasCardFilterActive = filters.participationSource === 'card' || filters.acceptedSource === 'card';
  const hasActiveFilters =
    filters.customer !== undefined || filters.participation !== undefined || filters.accepted !== undefined;

  return (
    <div className="flex flex-col justify-between gap-4 rounded-xl bg-white p-4 shadow-md md:flex-row md:flex-wrap md:items-center">
      <div>
        <div className="text-lg">
          {t('core.customerWithCount', {
            count: totalCustomerCount,
            formattedCount: asNumber({ value: totalCustomerCount }),
          })}{' '}
          |{' '}
          {t('core.divisionWithCount', {
            count: totalDivisionCount,
            formattedCount: asNumber({ value: totalDivisionCount }),
          })}
        </div>
        {hasActiveFilters && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-3xl bg-blue-50 py-2 pl-4 pr-2 font-medium">
            <div>
              {t('core.filteredTo')}{' '}
              {t('core.customerWithCount', {
                count: filteredCustomerCount,
                formattedCount: asNumber({ value: filteredCustomerCount }),
              })}{' '}
              |{' '}
              {t('core.divisionWithCount', {
                count: filteredDivisionCount,
                formattedCount: asNumber({ value: filteredDivisionCount }),
              })}
            </div>
            <Button onClick={handleClearFilters} size="sm" variant="secondary">
              <span className="flex items-center gap-1">
                {t('core.clearFilters')} <XmarkIcon className="size-6" />
              </span>
            </Button>
          </div>
        )}
      </div>
      <div className={cn('flex flex-col gap-2 md:flex-row', { 'opacity-38': hasCardFilterActive })}>
        <div className="w-full space-y-1 md:w-44">
          <Label htmlFor="customer-select" size="sm">
            {t('draftOffer.labels.customer')}
          </Label>
          <Select disabled={hasCardFilterActive} onValueChange={handleCustomerChange} value={filters.customer || 'all'}>
            <SelectTrigger id="customer-select" size="sm">
              <SelectValue placeholder={t('maker.all')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('maker.all')}</SelectItem>
              {availableCustomers.map((customer) => (
                <SelectItem key={customer.uuid} value={customer.uuid}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full space-y-1 md:w-44">
          <Label htmlFor="participation-select" size="sm">
            {t('core.participation')}
          </Label>
          <Select
            disabled={hasCardFilterActive}
            onValueChange={handleParticipationChange}
            value={participationDropdownValue}
          >
            <SelectTrigger id="participation-select" size="sm">
              <SelectValue placeholder={t('maker.all')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('maker.all')}</SelectItem>
              <SelectItem value={PARTICIPATION_FILTER.ON}>{t('core.participationOn')}</SelectItem>
              <SelectItem value={PARTICIPATION_FILTER.OFF}>{t('core.participationOff')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default OfferActivityFilters;
